import { Component, Input, OnChanges, ViewChild } from '@angular/core';
import { ChartConfiguration, ChartData } from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';
import { SolarHistoryPoint } from '../version.service';
import { chartPalette, timeTooltipTitle, lineLegendLabels, dragZoom, timeAxisTick } from '../shared/chart.utils';

@Component({
  selector: 'app-solar-chart',
  template: `
    <div style="position:relative;height:clamp(200px,55vh,700px);">
      <canvas baseChart
        [data]="chartData"
        [options]="chartOptions"
        type="line">
      </canvas>
      <button *ngIf="zoomed" class="btn btn-sm btn-outline-secondary"
              style="position:absolute;top:4px;right:4px;font-size:.65rem;padding:1px 6px;opacity:.85;z-index:2;"
              (click)="resetZoom()">↩ Reset Zoom</button>
    </div>
  `,
})
export class SolarChartComponent implements OnChanges {
  @ViewChild(BaseChartDirective) chartDirective?: BaseChartDirective;
  @Input() history: SolarHistoryPoint[] = [];
  @Input() darkMode = false;
  @Input() windowMs = 24 * 3600 * 1000;
  @Input() labelPv      = '';
  @Input() labelBattery = '';
  @Input() labelGrid    = '';
  @Input() labelTotal   = '';
  @Input() labelSoc     = '';

  zoomed = false;

  resetZoom(): void {
    (this.chartDirective?.chart as any)?.resetZoom();
    this.zoomed = false;
  }

  chartData: ChartData<'line'> = { datasets: [] };
  chartOptions: ChartConfiguration<'line'>['options'] = {};

  private formatW(w: number): string {
    const abs = Math.abs(w);
    return abs >= 1000 ? `${(w / 1000).toFixed(2)} kW` : `${Math.round(w)} W`;
  }

  ngOnChanges(): void {
    const { textColor, gridColor } = chartPalette(this.darkMode);
    const zeroLineColor = this.darkMode ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)';
    // x window spans the selected range so date labels show across week/month even
    // when the archive is still sparse.
    const xMax = Date.now();
    const xMin = xMax - this.windowMs;

    this.chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: { display: true, labels: lineLegendLabels(textColor) },
        tooltip: {
          callbacks: {
            title: timeTooltipTitle,
            label: (item) => {
              if ((item.dataset as any).yAxisID === 'y1') {
                return ` ${item.dataset.label}: ${Math.round(item.parsed.y ?? 0)} %`;
              }
              return ` ${item.dataset.label}: ${this.formatW(item.parsed.y ?? 0)}`;
            },
          },
        },
        zoom: dragZoom(() => { this.zoomed = true; }),
      },
      scales: {
        x: {
          type: 'linear',
          min: xMin,
          max: xMax,
          ticks: {
            color: textColor,
            maxTicksLimit: 9,
            callback: timeAxisTick(xMin, xMax),
          },
          grid: { color: gridColor },
        },
        y: {
          ticks: {
            color: textColor,
            callback: (val) => {
              const w = val as number;
              return Math.abs(w) >= 1000 ? `${(w / 1000).toFixed(1)} kW` : `${w} W`;
            },
          },
          grid: {
            color: (ctx) => ctx.tick.value === 0 ? zeroLineColor : gridColor,
          },
        },
        y1: {
          position: 'right',
          min: 0,
          max: 100,
          ticks: {
            color: textColor,
            callback: (val) => `${val} %`,
            maxTicksLimit: 6,
          },
          grid: { drawOnChartArea: false },
        },
      },
      elements: {
        point: { radius: 0, hoverRadius: 3 },
        line:  { borderWidth: 1.5, tension: 0.6 },
      },
    };

    this.chartData = {
      datasets: [
        {
          label: `☀️ ${this.labelPv}`,
          data: this.history.map(p => ({ x: p.ts, y: p.pvYieldW })),
          borderColor: '#f59e0b',
          backgroundColor: 'transparent',
          fill: false,
        },
        {
          label: `🔋 ${this.labelBattery}`,
          data: this.history.map(p => ({ x: p.ts, y: p.batteryDischargeW })),
          borderColor: '#22c55e',
          backgroundColor: 'transparent',
          fill: false,
        },
        {
          label: `🔌 ${this.labelGrid}`,
          data: this.history.map(p => ({ x: p.ts, y: p.gridConsumptionW })),
          borderColor: '#ef4444',
          backgroundColor: 'transparent',
          fill: false,
        },
        {
          label: `🏠 ${this.labelTotal}`,
          data: this.history.map(p => ({ x: p.ts, y: p.totalConsumptionW })),
          borderColor: '#0d6efd',
          backgroundColor: 'transparent',
          fill: false,
        },
        {
          label: `🔋 ${this.labelSoc}`,
          data: this.history.map(p => ({ x: p.ts, y: p.batteryPercent })),
          borderColor: '#a855f7',
          backgroundColor: 'transparent',
          fill: false,
          yAxisID: 'y1',
          borderDash: [4, 3],
        },
      ],
    };
  }
}
